/**
 * @mb-group/frontend — the app entry.
 *
 *   createFrontend(ctx).mount(el) / unmount() / navigate(routeKey, params)
 *
 * One frontend, one brand skin: the V1/V2 switching shell is gone (ADR 0012), so
 * this module owns the whole document — chrome, routing, overlays and theme.
 */
import "./styles/index.css";
import type { Locale } from "@mb-group/shared-types";
import type { I18n } from "@mb-group/i18n";
import { createStore, registerAll } from "@mb-group/ui-components";

import { clear, el } from "./core/dom.js";
import type { Analytics, Auth, GqlClient } from "./core/deps.js";
import { EDITOR_ROLES } from "./core/deps.js";
import type { AppContext, AppState, GateTarget } from "./core/context.js";
import { copyFor } from "./core/context.js";
import { Router } from "./core/router.js";
import {
	buildHref,
	type AppRouteKey,
	type QueryParams,
	type RouteMatch,
	type RouteParams,
} from "./core/routes.js";
import {
	applyTheme,
	onSystemThemeChange,
	readStoredPreference,
	resolveTheme,
} from "./core/theme.js";
import { ContentGateway, errorCode } from "./data/gateway.js";
import { typesetContent } from "./data/typography.js";
import type { Footer as FooterData, Navigation as NavData, ServiceCategory } from "./data/types.js";
import {
	demoBanner,
	footer as footerChrome,
	header as headerChrome,
	parentKey,
	skipLink,
} from "./components/layout.js";
import { assistantDialog, gateDialog, stickyAssistant } from "./components/overlays.js";

import { homeView } from "./views/home.js";
import { servicesListView, serviceDetailView } from "./views/services.js";
import { aboutView } from "./views/about.js";
import { industriesView } from "./views/industries.js";
import { companiesListView, companyDetailView } from "./views/companies.js";
import { caseStudiesListView, caseStudyDetailView } from "./views/case-studies.js";
import { knowledgeListView, articleDetailView } from "./views/knowledge.js";
import { resourcesView, webinarsView } from "./views/resources.js";
import { landingView, formPageView } from "./views/pages.js";
import { searchView } from "./views/search.js";
import { contactView } from "./views/contact.js";
import { privacyView, cookiesView } from "./views/legal.js";
import {
	errorView,
	notFoundView,
	previewForbiddenView,
	previewUnauthorizedView,
} from "./views/status.js";
import { panelView } from "./views/panel.js";

export interface FrontendContext {
	client: GqlClient;
	i18n: I18n;
	auth: Auth;
	analytics: Analytics;
	site: string;
	/** Strapi admin panel, linked from /panel; omitted = no link. */
	cmsUrl?: string;
	/** Content panel on its own host; set = /panel redirects there. */
	panelUrl?: string;
}

export interface FrontendApp {
	mount(el: HTMLElement): void | Promise<void>;
	unmount(): void;
	navigate(routeKey: string, params?: Record<string, string>): void;
}

export function createFrontend(fctx: FrontendContext): FrontendApp {
	registerAll();

	const initialPref = readStoredPreference();
	const initialState: AppState = {
		demo: false,
		themePreference: initialPref,
		theme: resolveTheme(initialPref),
		menuOpen: false,
		megaOpen: false,
		assistantOpen: false,
		assistantQuery: "",
		gate: null,
		gateSent: false,
		loading: false,
	};
	const store = createStore(initialState);

	const getLocale = (): Locale => fctx.i18n.locale;
	const gateway = new ContentGateway(fctx.client, fctx.site, getLocale, (demo) =>
		store.set({ demo }),
	);

	let router: Router | null = null;
	let rootEl: HTMLElement | null = null;
	let mainEl: HTMLElement | null = null;
	let headerHost: HTMLElement | null = null;
	let footerHost: HTMLElement | null = null;
	let bannerHost: HTMLElement | null = null;
	let overlayHost: HTMLElement | null = null;
	let navData: NavData | null = null;
	let footerData: FooterData | null = null;
	let categories: ServiceCategory[] = [];
	const disposers: (() => void)[] = [];

	const ctx: AppContext = {
		client: fctx.client,
		auth: fctx.auth,
		analytics: fctx.analytics,
		i18n: fctx.i18n,
		site: fctx.site,
		...(fctx.cmsUrl ? { cmsUrl: fctx.cmsUrl } : {}),
		...(fctx.panelUrl ? { panelUrl: fctx.panelUrl } : {}),
		gateway,
		store,
		get router() {
			if (!router) throw new Error("frontend: router accessed before mount()");
			return router;
		},
		locale: getLocale,
		// The frozen @mb-group/i18n chrome gets its hard spaces here — the one
		// place every ctx.t() passes through (see data/typography.ts).
		t: (key, params) => typesetContent(fctx.i18n.t(key, params), getLocale()),
		copy: () => copyFor(getLocale()),
		track: (event) => {
			try {
				fctx.analytics.track(event);
			} catch {
				/* analytics must never break the UI */
			}
		},
		navigate: (key, params, query) => router?.navigate(key, params, query),
		href: (key, params, query) => buildHref(key, getLocale(), params, query),
		openAssistant: (query) => {
			store.set({
				assistantOpen: true,
				menuOpen: false,
				megaOpen: false,
				...(query !== undefined ? { assistantQuery: query } : {}),
			});
		},
		openGate: (target: GateTarget) => store.set({ gate: target, gateSent: false }),
	};

	// --- chrome --------------------------------------------------------------
	async function refreshChrome(): Promise<void> {
		const [nav, foot, cats] = await Promise.all([
			gateway.navigation(),
			gateway.footer(),
			gateway.serviceCategories(),
		]);
		navData = nav;
		footerData = foot;
		categories = cats;
		renderChromeDom();
	}

	function renderChromeDom(): void {
		if (headerHost && navData) {
			clear(headerHost);
			headerHost.appendChild(headerChrome(ctx, navData, categories));
		}
		if (footerHost && footerData) {
			clear(footerHost);
			footerHost.appendChild(footerChrome(ctx, footerData, categories));
		}
		if (bannerHost) {
			clear(bannerHost);
			bannerHost.appendChild(demoBanner(ctx));
		}
		applyActiveNav();
	}

	function applyActiveNav(): void {
		const current = router?.getCurrent();
		const activeKey = current ? parentKey(current.key) : null;
		for (const host of [headerHost, footerHost]) {
			host?.querySelectorAll<HTMLElement>("[data-routekey]").forEach((linkEl) => {
				const isActive = linkEl.getAttribute("data-routekey") === activeKey;
				if (isActive) linkEl.setAttribute("aria-current", "page");
				else linkEl.removeAttribute("aria-current");
			});
		}
	}

	/**
	 * Reflect menu/overlay state onto the existing chrome instead of re-rendering
	 * it — a re-render on every toggle would drop the user's focus position.
	 */
	function syncChromeState(state: AppState): void {
		const headerEl = headerHost?.firstElementChild as HTMLElement | null;
		if (headerEl) {
			headerEl.setAttribute("data-mega-open", String(state.megaOpen));
			headerEl
				.querySelector(".mb-nav__trigger")
				?.setAttribute("aria-expanded", String(state.megaOpen));
			headerEl.querySelector(".mb-burger")?.setAttribute("aria-expanded", String(state.menuOpen));
			const mobile = headerEl.querySelector<HTMLElement>(".mb-mobilemenu");
			if (mobile) mobile.hidden = !state.menuOpen;
		}
		const banner = bannerHost?.firstElementChild as HTMLElement | null;
		if (banner) banner.hidden = !state.demo;

		document.documentElement.classList.toggle(
			"mb-noscroll",
			state.assistantOpen || state.gate !== null || state.menuOpen,
		);
	}

	/** Mount/unmount the dialogs, restoring focus to whatever opened them. */
	let lastFocused: HTMLElement | null = null;
	let assistantMounted = false;
	let gateMountedFor: GateTarget | "sent" | null = null;

	/**
	 * The "mounted" bookkeeping is updated BEFORE the dialog factory runs. A
	 * factory may touch the store while building (the assistant seeds its result
	 * list, which writes the query), that notifies subscribers, and this function
	 * runs again — with the flag still unset it would build a second dialog, and
	 * recurse until the stack blew.
	 */
	function syncOverlays(state: AppState): void {
		if (!overlayHost) return;

		if (state.assistantOpen && !assistantMounted) {
			assistantMounted = true;
			lastFocused = document.activeElement as HTMLElement | null;
			overlayHost.appendChild(assistantDialog(ctx));
		} else if (!state.assistantOpen && assistantMounted) {
			assistantMounted = false;
			overlayHost.querySelectorAll(".mb-dialog").forEach((node) => {
				if (!node.querySelector(".mb-gate")) node.remove();
			});
			lastFocused?.focus();
		}

		const gateKey: GateTarget | "sent" | null =
			state.gate === null ? null : state.gateSent ? "sent" : state.gate;
		if (gateKey !== gateMountedFor) {
			const wasClosed = gateMountedFor === null;
			gateMountedFor = gateKey;
			// Remove the previous gate panel (the assistant dialog, if any, stays).
			overlayHost.querySelectorAll(".mb-dialog").forEach((node) => {
				if (node.querySelector(".mb-gate")) node.remove();
			});
			if (gateKey !== null) {
				if (wasClosed) lastFocused = document.activeElement as HTMLElement | null;
				const panel = gateDialog(ctx);
				if (panel) overlayHost.appendChild(panel);
			} else {
				lastFocused?.focus();
			}
		}
	}

	// --- routing dispatch ----------------------------------------------------
	async function dispatch(match: RouteMatch | null): Promise<HTMLElement> {
		if (!match) return notFoundView(ctx);
		switch (match.key) {
			case "home":
				return homeView(ctx);
			case "services":
				return servicesListView(ctx, match);
			case "serviceDetail":
				return serviceDetailView(ctx, match);
			case "about":
				return aboutView(ctx);
			case "industries":
				return industriesView(ctx);
			case "companies":
				return companiesListView(ctx);
			case "companyDetail":
				return companyDetailView(ctx, match);
			case "caseStudies":
				return caseStudiesListView(ctx);
			case "caseStudyDetail":
				// A draft renders only under /podglad and /preview, behind the sign-in
				// and the draft strip: the public page ignores a stray ?preview=true.
				return caseStudyDetailView(ctx, {
					...match,
					query: { ...match.query, preview: undefined },
				});
			case "knowledge":
				return knowledgeListView(ctx, match);
			case "article":
				return articleDetailView(ctx, match);
			case "resources":
				return resourcesView(ctx);
			case "webinars":
				return webinarsView(ctx);
			case "landing":
				return landingView(ctx, match);
			case "search":
				return searchView(ctx, match);
			case "contact":
				return contactView(ctx);
			case "privacy":
				return privacyView(ctx);
			case "cookies":
				return cookiesView(ctx);
			case "formPage":
				return formPageView(ctx, match);
			case "preview":
				return previewDispatch(match);
			case "panel":
				return panelView(ctx, match);
			default:
				return notFoundView(ctx);
		}
	}

	async function previewDispatch(match: RouteMatch): Promise<HTMLElement> {
		if (!ctx.auth.isAuthenticated()) return previewUnauthorizedView(ctx);
		const roles = ctx.auth.getRoles();
		if (!roles.some((r) => (EDITOR_ROLES as readonly string[]).includes(r))) {
			return previewForbiddenView(ctx);
		}

		try {
			return await previewContent(match);
		} catch (err) {
			// The gateway has the last word on who may read a draft (its editor
			// roles are not this app's EDITOR_ROLES: no translator there).
			const code = errorCode(err);
			if (code === "UNAUTHENTICATED") return previewUnauthorizedView(ctx);
			if (code === "FORBIDDEN") return previewForbiddenView(ctx);
			throw err;
		}
	}

	async function previewContent(match: RouteMatch): Promise<HTMLElement> {
		const type = match.params.type ?? "";
		const slug = match.params.slug ?? "";
		const synthetic = (key: AppRouteKey): RouteMatch => ({
			key,
			locale: match.locale,
			params: { slug },
			query: { preview: "true" },
		});

		let inner: HTMLElement;
		switch (type) {
			case "service":
				inner = await serviceDetailView(ctx, synthetic("serviceDetail"));
				break;
			case "company":
				inner = await companyDetailView(ctx, synthetic("companyDetail"));
				break;
			case "case_study":
			case "case-study":
				inner = await caseStudyDetailView(ctx, synthetic("caseStudyDetail"));
				break;
			case "article":
				inner = await articleDetailView(ctx, synthetic("article"));
				break;
			default:
				inner = notFoundView(ctx);
		}

		const wrap = el("div", { class: "mb-view mb-view--preview" });
		wrap.appendChild(
			el(
				"div",
				{ class: "mb-draftstrip", role: "note" },
				el(
					"span",
					{ class: "mb-pill mb-pill--accent" },
					ctx.locale() === "en" ? "Draft" : "Wersja robocza",
				),
			),
		);
		wrap.appendChild(inner);
		return wrap;
	}

	async function renderRoute(match: RouteMatch | null): Promise<void> {
		if (!mainEl) return;
		const state = store.get();
		if (state.menuOpen || state.megaOpen) store.set({ menuOpen: false, megaOpen: false });
		ctx.track({
			type: "page_view",
			route: match?.key ?? "not_found",
			path: window.location.pathname,
		});

		let content: HTMLElement;
		try {
			content = await dispatch(match);
		} catch (err) {
			// Never swallow silently: a bare `catch` here once hid a TypeError behind
			// the generic 500 view, leaving the browser console completely clean.
			console.error("[frontend] route render failed", {
				route: match?.key ?? "not_found",
				path: window.location.pathname,
				error: err,
			});
			content = errorView(ctx, () => void renderRoute(router?.getCurrent() ?? null));
		}
		clear(mainEl);
		mainEl.appendChild(content);
		applyActiveNav();
		mainEl.focus({ preventScroll: true });
		// An in-page anchor (e.g. /uslugi#kategoria-…) is handled by the view itself.
		if (!window.location.hash) window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
	}

	// --- mount / unmount -----------------------------------------------------
	const app: FrontendApp = {
		async mount(host) {
			rootEl = host;
			applyTheme(store.get().themePreference);
			document.documentElement.lang = getLocale();

			clear(host);
			bannerHost = el("div", { class: "mb-bannerhost" });
			headerHost = el("div", { class: "mb-headerhost" });
			footerHost = el("div", { class: "mb-footerhost" });
			overlayHost = el("div", { class: "mb-overlayhost" });
			mainEl = el("main", { id: "mb-main", class: "mb-main", tabindex: "-1" });

			host.appendChild(skipLink(ctx));
			host.appendChild(bannerHost);
			host.appendChild(headerHost);
			host.appendChild(mainEl);
			host.appendChild(footerHost);
			host.appendChild(stickyAssistant(ctx));
			host.appendChild(overlayHost);

			router = new Router(host, {
				getLocale,
				onLocaleInferred: (locale) => {
					if (locale !== fctx.i18n.locale) {
						fctx.i18n.setLocale(locale);
						document.documentElement.lang = locale;
						void refreshChrome();
					}
				},
				render: (match) => renderRoute(match),
			});

			disposers.push(
				store.subscribe((state) => {
					syncChromeState(state);
					syncOverlays(state);
				}),
			);

			// Follow the OS theme while the preference is "system".
			disposers.push(
				onSystemThemeChange((mode) => {
					if (store.get().themePreference === "system") {
						applyTheme("system");
						store.set({ theme: mode });
					}
				}),
			);

			// Cmd/Ctrl+K opens the assistant, the way the mock-up's search does.
			const onKey = (event: KeyboardEvent) => {
				if ((event.key === "k" || event.key === "K") && (event.metaKey || event.ctrlKey)) {
					event.preventDefault();
					ctx.openAssistant();
				}
			};
			window.addEventListener("keydown", onKey);
			disposers.push(() => window.removeEventListener("keydown", onKey));

			await refreshChrome();
			void gateway.probe();
			router.start();
		},

		unmount() {
			router?.stop();
			router = null;
			for (const dispose of disposers.splice(0)) dispose();
			document.documentElement.classList.remove("mb-noscroll");
			if (rootEl) clear(rootEl);
			rootEl = mainEl = headerHost = footerHost = bannerHost = overlayHost = null;
			assistantMounted = false;
			gateMountedFor = null;
		},

		navigate(routeKey, params) {
			router?.navigate(routeKey as AppRouteKey, params);
		},
	};

	return app;
}

export type { AppContext } from "./core/context.js";
export type { QueryParams, RouteParams };
